import React, { createContext, useContext, useEffect, useState } from "react";
import type { Account, User, Workspace, PermissionGroup } from "../lib/types";
import { getWorkspace, localWorkspace } from "../lib/api";
import { capabilitiesFor } from "../lib/capabilities";

const LS_VIEW_AS = "penumbrai:viewAsUserId";

interface Ctx {
  ws: Workspace;
  me: User;
  /** capabilities granted to `me` via their permission group */
  caps: Set<string>;
  can: (capId: string) => boolean;
  groups: PermissionGroup[];
  /** demo "view as" — impersonate another user's access without a real login */
  viewAsUserId: string | null;
  setViewAs: (userId: string | null) => void;
  accountById: (id: string) => Account | undefined;
  userById: (id: string) => User | undefined;
}

const WorkspaceCtx = createContext<Ctx | null>(null);

function readViewAs(): string | null {
  try { return localStorage.getItem(LS_VIEW_AS); } catch { return null; }
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [ws, setWs] = useState<Workspace>(localWorkspace);
  const [viewAsUserId, setViewAsState] = useState<string | null>(readViewAs());

  useEffect(() => {
    let alive = true;
    getWorkspace().then((w) => {
      if (alive) setWs(w);
    });
    return () => {
      alive = false;
    };
  }, []);

  const setViewAs = (userId: string | null) => {
    try { userId ? localStorage.setItem(LS_VIEW_AS, userId) : localStorage.removeItem(LS_VIEW_AS); } catch { /* ignore */ }
    setViewAsState(userId);
  };

  const groups = ws.permissionGroups ?? [];
  const baseUser = ws.users.find((u) => u.id === ws.currentUserId) ?? ws.users[0];
  const me = (viewAsUserId ? ws.users.find((u) => u.id === viewAsUserId) : undefined) ?? baseUser;
  const caps = capabilitiesFor(me, groups);

  const value: Ctx = {
    ws,
    me,
    caps,
    can: (capId) => caps.has(capId),
    groups,
    viewAsUserId,
    setViewAs,
    accountById: (id) => ws.accounts.find((a) => a.id === id),
    userById: (id) => ws.users.find((u) => u.id === id),
  };

  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}

export function useWorkspace(): Ctx {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) throw new Error("useWorkspace must be used within WorkspaceProvider");
  return ctx;
}
